import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, X, Filter as FilterIcon, Link2, Pencil, Trash2, ExternalLink,
  Calendar, Search, ArrowUpRight, ArrowDownRight, Minus, Info, Layers, Download,
} from "lucide-react";

// Canonical picker order matches the "Add SEO Activity" modal spec.
const TYPE_OPTIONS = [
  "backlink_built", "guest_post_published", "backlink_indexed",
  "onpage_update", "title_meta_updated", "content_refresh",
  "internal_links_added", "schema_added", "competitor_note",
  "google_update_note", "custom_note",
];

// Visual metadata per activity type. Legacy short keys (on_page/title_meta/…)
// are aliased so activities created before the modal upgrade still render.
const TYPE_META = {
  // Canonical
  backlink_built:        { label: "Backlink Built",        tone: "accent"  },
  guest_post_published:  { label: "Guest Post Published",  tone: "accent"  },
  backlink_indexed:      { label: "Backlink Indexed",      tone: "success" },
  onpage_update:         { label: "On-Page SEO Update",    tone: "info"    },
  title_meta_updated:    { label: "Title / Meta Updated",  tone: "info"    },
  content_refresh:       { label: "Content Refresh",       tone: "info"    },
  internal_links_added:  { label: "Internal Links Added",  tone: "info"    },
  schema_added:          { label: "Schema Added",          tone: "info"    },
  competitor_note:       { label: "Competitor Note",       tone: "warning" },
  google_update_note:    { label: "Google Update Note",    tone: "warning" },
  custom_note:           { label: "Custom Note",           tone: "muted"   },
  // Legacy aliases
  on_page:               { label: "On-Page SEO Update",    tone: "info"    },
  title_meta:            { label: "Title / Meta Updated",  tone: "info"    },
  internal_links:        { label: "Internal Links Added",  tone: "info"    },
  schema:                { label: "Schema Added",          tone: "info"    },
  google_update:         { label: "Google Update Note",    tone: "warning" },
  custom:                { label: "Custom Note",           tone: "muted"   },
};

const STATUS_OPTIONS = ["planned", "published", "indexed", "lost", "completed", "note_only"];
const STATUS_META = {
  planned:   { label: "Planned",   cls: "bg-slate-100 text-slate-600 border-slate-200" },
  published: { label: "Published", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  indexed:   { label: "Indexed",   cls: "bg-[#E0E7FF] text-[#0047FF] border-[#0047FF]/20" },
  lost:      { label: "Lost",      cls: "bg-red-50 text-red-700 border-red-200" },
  completed: { label: "Completed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  note_only: { label: "Note Only", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  // Legacy
  note:      { label: "Note",      cls: "bg-amber-50 text-amber-800 border-amber-200" },
};

// Types that share the small generic backlink form (referring URL, supplier,
// anchor, published, indexed). guest_post_published gets a much richer form
// so it's intentionally excluded here.
// Types that share the small generic backlink form (referring URL, supplier,
// anchor, published, indexed). `guest_post_published` and `backlink_built`
// get a much richer form so they're excluded here.
const BACKLINK_TYPES = new Set(["backlink_indexed"]);
const GUEST_POST_TYPE = "guest_post_published";
const BACKLINK_BUILT_TYPE = "backlink_built";
// Both types render the same rich, sectioned form / card.
const RICH_LINK_TYPES = new Set([GUEST_POST_TYPE, BACKLINK_BUILT_TYPE]);

// On-Page SEO Update form — spec-defined dropdowns for the type-specific section.
const ONPAGE_UPDATE_TYPES = [
  "Title tag updated", "Meta description updated", "H1 updated",
  "Headings improved", "Content refreshed", "Content expanded",
  "FAQ added", "FAQ schema added", "Schema updated",
  "Internal links added", "Images optimized", "Alt text updated",
  "Canonical updated", "URL updated", "Author/E-E-A-T section updated",
  "CTA section updated", "Custom update",
];
const ONPAGE_REASONS = [
  "Ranking dropped", "Ranking stagnated", "Improve CTR", "Search intent shift",
  "Competitor outranked us", "After Google update", "Content outdated",
  "Content too thin", "Improve snippet visibility", "Routine refresh",
  "Client request", "Other",
];
const ONPAGE_OUTCOMES = [
  "Improve ranking", "Improve CTR", "Improve content depth",
  "Match search intent better", "Improve topical relevance",
  "Improve featured snippet chance", "Improve conversion",
  "Recover from ranking loss", "Other",
];
const SCHEMA_TYPES = ["FAQ", "Article", "Breadcrumb", "HowTo", "Organization", "Product", "Review", "Other"];
const PRIORITIES = ["Low", "Medium", "High"];

// Guest Post activity — spec-defined dropdown options for the type-specific form.
const LINK_TYPES        = ["DoFollow", "NoFollow", "Sponsored", "UGC", "Other"];
const CURRENCIES        = ["USD", "MYR", "EUR", "GBP", "SGD", "AUD", "Other"];
const INDEXED_STATUSES  = ["Unknown", "Not Indexed", "Indexed", "Lost", "Pending Check"];
const INDEXING_METHODS  = ["Natural", "Google Search Console", "Indexing Service", "Manual Submission", "Other"];
const LINK_POSITIONS    = ["Top", "Middle", "Bottom", "Author Bio", "Sidebar", "Other"];
const CONTEXT_OPTIONS   = ["Contextual", "Author Bio", "Resource Section", "Mention Only", "Other"];
const RELEVANCE_OPTIONS = ["High", "Medium", "Low"];
const YES_NO            = ["Yes", "No"];
const YES_NO_UNKNOWN    = ["Yes", "No", "Unknown"];
// Backlink Type — only relevant for the `backlink_built` activity form.
const BACKLINK_KIND_OPTIONS = [
  "Niche Edit", "Directory", "HARO", "Forum", "Profile", "Web 2.0", "Citation",
  "PBN", "Blog Comment", "Resource Page", "Image Link", "Press Release",
  "Social Profile", "Other",
];

const TONE_CLS = {
  accent:  "bg-[#E0E7FF] text-[#0047FF] border-[#0047FF]/25",
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  info:    "bg-sky-50 text-sky-700 border-sky-200",
  warning: "bg-amber-50 text-amber-800 border-amber-200",
  muted:   "bg-slate-100 text-slate-600 border-slate-200",
};

function typeLabel(key) { return TYPE_META[key]?.label || key; }
function typeTone(key)  { return TONE_CLS[TYPE_META[key]?.tone || "muted"]; }

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
}

function groupByDate(rows) {
  const map = new Map();
  rows.forEach((r) => {
    const key = (r.occurred_at || "").slice(0, 10);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(r);
  });
  return Array.from(map.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
}

export default function ActivityTimeline({ projectId, project, keywords, canEdit }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [editing, setEditing] = useState(null);
  const [allKeywords, setAllKeywords] = useState(keywords || []);
  useEffect(() => {
    api.get("/keywords", { params: { project_id: projectId } })
      .then((r) => setAllKeywords(r.data || []))
      .catch(() => setAllKeywords(keywords || []));
  }, [projectId]);

  const [filters, setFilters] = useState({
    activity_type: "", keyword_id: "", status: "", date_from: "", date_to: "", q: "",
  });

  const load = () => {
    setLoading(true);
    const params = { project_id: projectId };
    if (filters.activity_type) params.activity_type = filters.activity_type;
    if (filters.keyword_id)    params.keyword_id    = filters.keyword_id;
    if (filters.status)        params.status        = filters.status;
    if (filters.date_from)     params.date_from     = filters.date_from;
    if (filters.date_to)       params.date_to       = filters.date_to;
    return api.get("/activities", { params })
      .then((r) => setRows(r.data || []))
      .catch(() => toast.error("Failed to load activities."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [projectId, filters.activity_type, filters.keyword_id, filters.status, filters.date_from, filters.date_to]);

  const clientFiltered = useMemo(() => {
    if (!filters.q.trim()) return rows;
    const q = filters.q.toLowerCase();
    return rows.filter((r) =>
      (r.title || "").toLowerCase().includes(q) ||
      (r.note || "").toLowerCase().includes(q) ||
      (r.page_url || "").toLowerCase().includes(q) ||
      (r.source_url || "").toLowerCase().includes(q) ||
      (r.supplier || "").toLowerCase().includes(q) ||
      (r.anchor_text || "").toLowerCase().includes(q) ||
      (r.keyword || "").toLowerCase().includes(q),
    );
  }, [rows, filters.q]);

  const grouped = useMemo(() => groupByDate(clientFiltered), [clientFiltered]);

  const clearFilters = () => setFilters({ activity_type: "", keyword_id: "", status: "", date_from: "", date_to: "", q: "" });
  const activeFilterCount = Object.values(filters).filter((v) => v !== "").length;

  const onDelete = async (row) => {
    if (!window.confirm(`Delete "${row.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/activities/${row.id}`);
      toast.success("Activity deleted.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
  };

  const openCreate = () => { setEditing(null); setShowModal(true); };
  const openEdit   = (row) => { setEditing(row); setShowModal(true); };

  return (
    <div className="mt-6" data-testid="activity-timeline">
      {/* Header */}
      <div className="card-crisp p-4 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-lg font-display font-semibold text-[#0A0D14]">SEO Activity Timeline</div>
          <div className="text-xs text-[#8A94A6] mt-1">
            Log the SEO work you did (backlinks, on-page edits, content refreshes). See it side-by-side with ranking movement.
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <button
              data-testid="activity-bulk-open"
              onClick={() => setShowBulk(true)}
              className="btn-ghost px-3 py-2 rounded-md text-sm inline-flex items-center gap-1"
            >
              <Layers className="h-3.5 w-3.5" /> Bulk Add Backlinks
            </button>
            <button
              data-testid="activity-log-open"
              onClick={openCreate}
              className="btn-primary px-3 py-2 rounded-md text-sm inline-flex items-center gap-1"
            >
              <Plus className="h-3.5 w-3.5" /> Add Activity
            </button>
          </div>
        )}
      </div>

      {/* Filter bar */}
      <div className="mt-4 card-crisp p-4 space-y-3">
        <div className="grid md:grid-cols-5 gap-3">
          <div className="md:col-span-2 relative">
            <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#8A94A6]" />
            <input
              data-testid="activity-filter-q"
              placeholder="Search title, note, URL, keyword…"
              className="w-full pl-9 pr-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            />
          </div>
          <select
            data-testid="activity-filter-type"
            value={filters.activity_type}
            onChange={(e) => setFilters({ ...filters, activity_type: e.target.value })}
            className="px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white min-w-0"
          >
            <option value="">All types</option>
            {TYPE_OPTIONS.map((k) => <option key={k} value={k}>{TYPE_META[k].label}</option>)}
          </select>
          <select
            data-testid="activity-filter-keyword"
            value={filters.keyword_id}
            onChange={(e) => setFilters({ ...filters, keyword_id: e.target.value })}
            className="px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white min-w-0"
          >
            <option value="">All keywords</option>
            {(allKeywords || []).map((k) => <option key={k.id} value={k.id}>{k.keyword}</option>)}
          </select>
          <select
            data-testid="activity-filter-status"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white min-w-0"
          >
            <option value="">Any status</option>
            {STATUS_OPTIONS.map((k) => <option key={k} value={k}>{STATUS_META[k].label}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col">
            <label className="text-[10px] uppercase tracking-wider text-[#8A94A6] font-medium mb-1">From</label>
            <input
              type="date"
              data-testid="activity-filter-from"
              value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              className="w-44 px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-[10px] uppercase tracking-wider text-[#8A94A6] font-medium mb-1">To</label>
            <input
              type="date"
              data-testid="activity-filter-to"
              value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              className="w-44 px-3 py-2 border border-[#E2E8F0] rounded-md text-sm"
            />
          </div>
        </div>
        {activeFilterCount > 0 && (
          <div className="flex items-center gap-2 text-xs text-[#525B6A]">
            <FilterIcon className="h-3 w-3" />
            {activeFilterCount} filter{activeFilterCount === 1 ? '' : 's'} active
            <button onClick={clearFilters} data-testid="activity-filter-clear" className="ml-2 text-[#0047FF] hover:underline">Clear</button>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="mt-6">
        {loading ? (
          <div className="card-crisp p-10 text-center text-sm text-[#8A94A6]" data-testid="activity-loading">Loading activities…</div>
        ) : grouped.length === 0 ? (
          <div className="card-crisp p-12 text-center space-y-4" data-testid="activity-empty">
            <div className="mx-auto h-12 w-12 rounded-full bg-[#0047FF]/[0.08] text-[#0047FF] inline-flex items-center justify-center">
              <Calendar className="h-6 w-6" />
            </div>
            {activeFilterCount > 0 ? (
              <>
                <div className="text-base font-display font-semibold text-[#0A0D14]">No activities match these filters</div>
                <div className="text-sm text-[#525B6A] max-w-md mx-auto">Try clearing filters, or record a new activity to keep the ranking story complete.</div>
                <button onClick={clearFilters} className="btn-ghost px-3 py-1.5 rounded-md text-sm">Clear filters</button>
              </>
            ) : (
              <>
                <div className="text-base font-display font-semibold text-[#0A0D14]">No SEO activities recorded yet</div>
                <div className="text-sm text-[#525B6A] max-w-md mx-auto">
                  Start logging backlinks, guest posts, on-page changes, and important notes to track ranking changes with more context.
                </div>
                {canEdit && (
                  <button
                    data-testid="activity-empty-add"
                    onClick={openCreate}
                    className="btn-primary px-4 py-2 rounded-md text-sm inline-flex items-center gap-1.5"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add First Activity
                  </button>
                )}
              </>
            )}
          </div>
        ) : (
          <ol className="relative border-l border-[#E2E8F0] ml-4 space-y-8" data-testid="activity-list">
            {grouped.map(([day, items]) => (
              <li key={day} className="pl-6 relative">
                <span className="absolute -left-[7px] top-1.5 h-3 w-3 rounded-full bg-[#0047FF] ring-4 ring-white" />
                <div className="text-xs uppercase tracking-wider text-[#8A94A6] font-medium flex items-center gap-2">
                  <Calendar className="h-3 w-3" />
                  {fmtDate(day)}
                  <span className="text-[#8A94A6]/60">·</span>
                  <span className="tabular-nums">{items.length} activit{items.length === 1 ? 'y' : 'ies'}</span>
                </div>
                <div className="mt-3 space-y-3">
                  {items.map((row) => (
                    RICH_LINK_TYPES.has(row.activity_type)
                      ? <GuestPostCard
                          key={row.id}
                          row={row}
                          kind={row.activity_type}
                          canEdit={canEdit}
                          onEdit={() => openEdit(row)}
                          onDelete={() => onDelete(row)}
                        />
                      : <ActivityCard
                          key={row.id}
                          row={row}
                          canEdit={canEdit}
                          onEdit={() => openEdit(row)}
                          onDelete={() => onDelete(row)}
                        />
                  ))}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      {showModal && (
        <ActivityModal
          projectId={projectId}
          projectName={project?.name || project?.domain || ""}
          keywords={allKeywords}
          initial={editing}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSaved={({ closeAfter }) => {
            if (closeAfter) { setShowModal(false); setEditing(null); }
            load();
          }}
        />
      )}
      {showBulk && (
        <BulkAddBacklinksModal
          projectId={projectId}
          projectName={project?.name || project?.domain || ""}
          onClose={() => setShowBulk(false)}
          onSaved={({ keepOpen } = {}) => { if (!keepOpen) setShowBulk(false); load(); }}
        />
      )}
    </div>
  );
}

function ActivityCard({ row, canEdit, onEdit, onDelete }) {
  const ctx = row.ranking_context;
  const statusMeta = row.status ? STATUS_META[row.status] : null;
  return (
    <div className="card-crisp p-4" data-testid={`activity-card-${row.id}`}>
      <div className="flex items-start gap-3">
        <span className={`shrink-0 inline-flex items-center text-[10px] px-2 py-0.5 rounded border font-medium uppercase tracking-wider ${typeTone(row.activity_type)}`}>
          {typeLabel(row.activity_type)}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-[#0A0D14] break-words">{row.title}</div>
          <div className="mt-1 flex items-center gap-3 text-xs text-[#8A94A6] flex-wrap">
            {row.keyword && (
              <span className="inline-flex items-center gap-1 text-[#0047FF]" data-testid={`activity-kw-${row.id}`}>
                <Link2 className="h-3 w-3" /> {row.keyword}
              </span>
            )}
            {statusMeta && (
              <span className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded border font-medium uppercase tracking-wider ${statusMeta.cls}`}>
                {statusMeta.label}
              </span>
            )}
            {row.supplier && <span>· Supplier: <span className="text-[#525B6A]">{row.supplier}</span></span>}
            {row.anchor_text && <span>· Anchor: <span className="text-[#525B6A]">&ldquo;{row.anchor_text}&rdquo;</span></span>}
          </div>
          {(row.page_url || row.source_url) && (
            <div className="mt-2 space-y-1 text-xs">
              {row.page_url && (
                <div className="flex items-center gap-1 text-[#525B6A]">
                  <span className="text-[10px] uppercase tracking-wider text-[#8A94A6]">Target</span>
                  <a href={row.page_url} target="_blank" rel="noreferrer" className="text-[#0047FF] hover:underline break-all inline-flex items-center gap-0.5">
                    {row.page_url} <ExternalLink className="h-3 w-3 shrink-0" />
                  </a>
                </div>
              )}
              {row.source_url && (
                <div className="flex items-center gap-1 text-[#525B6A]">
                  <span className="text-[10px] uppercase tracking-wider text-[#8A94A6]">Referring</span>
                  <a href={row.source_url} target="_blank" rel="noreferrer" className="text-[#0047FF] hover:underline break-all inline-flex items-center gap-0.5">
                    {row.source_url} <ExternalLink className="h-3 w-3 shrink-0" />
                  </a>
                </div>
              )}
            </div>
          )}
          {row.note && (
            <div className="mt-2 text-sm text-[#525B6A] whitespace-pre-wrap break-words">{row.note}</div>
          )}
          {ctx && (ctx.before || ctx.after) && (
            <RankingContextStrip ctx={ctx} />
          )}
          {row.impact_tracking && row.impact_tracking.enabled && <ImpactCard impact={row.impact_tracking} />}
          {row.author_email && (
            <div className="mt-2 text-[10px] text-[#8A94A6] uppercase tracking-wider">
              Logged by {row.author_email} · {fmtDate(row.created_at)}
            </div>
          )}
        </div>
        {canEdit && (
          <div className="flex items-center gap-1 shrink-0">
            <button data-testid={`activity-edit-${row.id}`} onClick={onEdit} className="p-1.5 text-[#8A94A6] hover:text-[#0047FF] hover:bg-[#F8F9FA] rounded" title="Edit">
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button data-testid={`activity-delete-${row.id}`} onClick={onDelete} className="p-1.5 text-[#8A94A6] hover:text-red-600 hover:bg-red-50 rounded" title="Delete">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function RankingContextStrip({ ctx }) {
  const b = ctx.before, a = ctx.after;
  let dir = null, delta = null;
  if (b && a && b.rank != null && a.rank != null) {
    if (a.rank < b.rank) { dir = "up"; delta = b.rank - a.rank; }
    else if (a.rank > b.rank) { dir = "down"; delta = a.rank - b.rank; }
    else { dir = "flat"; delta = 0; }
  }
  return (
    <div className="mt-2 inline-flex items-center gap-2 text-xs bg-[#F8F9FA] border border-[#E2E8F0] px-2.5 py-1 rounded" data-testid="activity-rank-ctx">
      <span className="text-[10px] uppercase tracking-wider text-[#8A94A6]">Rank</span>
      <span className="tabular-nums font-medium text-[#525B6A]">{b?.rank != null ? `#${b.rank}` : "—"}</span>
      <span className="text-[#8A94A6]">→</span>
      <span className="tabular-nums font-medium text-[#0A0D14]">{a?.rank != null ? `#${a.rank}` : "—"}</span>
      {dir === "up"   && <span className="inline-flex items-center gap-0.5 text-emerald-600 tabular-nums"><ArrowUpRight className="h-3 w-3" />{delta}</span>}
      {dir === "down" && <span className="inline-flex items-center gap-0.5 text-red-600 tabular-nums"><ArrowDownRight className="h-3 w-3" />{delta}</span>}
      {dir === "flat" && <span className="inline-flex items-center gap-0.5 text-[#8A94A6] tabular-nums"><Minus className="h-3 w-3" />0</span>}
      <span className="text-[10px] text-[#8A94A6] italic ml-1">(observed, not attributed)</span>
    </div>
  );
}

// Blank form used on open + after "Save & Add Another".
function blankForm(defaults = {}) {
  const today = new Date().toISOString().slice(0, 10);
  return {
    activity_type: "",
    title:         "",
    activity_date: today,
    status:        "",
    keyword_id:    "",
    page_url:      "",
    referring_url: "",
    supplier:      "",
    anchor_text:   "",
    published_at:  "",
    indexed_at:    "",
    notes:         "",
    details:       {},
    // Activity Impact Tracking — opt-in for backlink / guest post only.
    impact_tracking_enabled: false,
    trigger_date_source:     "auto", // auto | published_date | indexed_date | activity_date
    checkpoints:             [7, 14, 30, 60],
    ...defaults,
  };
}

function formFromRow(row) {
  return {
    activity_type: row.activity_type || "",
    title:         row.title || "",
    activity_date: (row.occurred_at || "").slice(0, 10) || new Date().toISOString().slice(0, 10),
    status:        row.status || "",
    keyword_id:    row.keyword_id || "",
    page_url:      row.page_url || "",
    referring_url: row.source_url || "",
    supplier:      row.supplier || "",
    anchor_text:   row.anchor_text || "",
    published_at:  (row.published_at || "").slice(0, 10),
    indexed_at:    (row.indexed_at || "").slice(0, 10),
    notes:         row.note || "",
    details:       row.details || {},
    impact_tracking_enabled: !!row.impact_tracking_enabled,
    trigger_date_source:     row.trigger_date_source || "auto",
    checkpoints:             Array.isArray(row.checkpoints) && row.checkpoints.length > 0 ? row.checkpoints : [7, 14, 30, 60],
  };
}

function ActivityModal({ projectId, projectName, keywords, initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(() => initial ? formFromRow(initial) : blankForm());
  const [busy, setBusy] = useState(false);
  // Auto-open the backlink details block for backlink types, or if the record
  // already has any of those fields filled in.
  const backlinkFieldsFilled = !!(form.referring_url || form.supplier || form.anchor_text || form.published_at || form.indexed_at);
  const [showBacklinkDetails, setShowBacklinkDetails] = useState(
    BACKLINK_TYPES.has(form.activity_type) || backlinkFieldsFilled,
  );
  useEffect(() => {
    if (BACKLINK_TYPES.has(form.activity_type)) setShowBacklinkDetails(true);
  }, [form.activity_type]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setDetails = (patch) => setForm((f) => ({ ...f, details: { ...(f.details || {}), ...patch } }));

  // Strip empty strings from details so the DB doesn't get littered with "".
  const cleanDetails = (obj) => {
    const out = {};
    Object.entries(obj || {}).forEach(([k, v]) => {
      if (v === "" || v == null) return;
      if (Array.isArray(v) && v.length === 0) return;
      out[k] = v;
    });
    return out;
  };

  const buildPayload = () => {
    const details = cleanDetails(form.details);
    const impactEligible = form.activity_type === GUEST_POST_TYPE || form.activity_type === BACKLINK_BUILT_TYPE;
    return {
      activity_type: form.activity_type,
      title: form.title.trim(),
      occurred_at: form.activity_date,
      keyword_id: form.keyword_id || null,
      page_url:   form.page_url.trim()      || null,
      source_url: form.referring_url.trim() || null,
      supplier:   form.supplier.trim()      || null,
      anchor_text:form.anchor_text.trim()   || null,
      published_at: form.published_at || null,
      indexed_at:   form.indexed_at   || null,
      status:     form.status || null,
      note:       form.notes.trim() || null,
      details:    Object.keys(details).length > 0 ? details : null,
      // Only send impact-tracking fields when the activity type supports it,
      // so on-page / content updates never persist stale opt-in flags.
      impact_tracking_enabled: impactEligible ? !!form.impact_tracking_enabled : false,
      trigger_date_source:     impactEligible && form.impact_tracking_enabled ? (form.trigger_date_source || "auto") : null,
      checkpoints:             impactEligible && form.impact_tracking_enabled ? form.checkpoints : null,
    };
  };

  const validate = () => {
    if (!form.activity_type)     { toast.error("Please choose an activity type."); return false; }
    if (!form.title.trim())      { toast.error("Title is required."); return false; }
    if (!form.activity_date)     { toast.error("Activity Date is required."); return false; }
    if (form.activity_type === "onpage_update") {
      if (!form.details?.update_type)       { toast.error("Update Type is required for on-page updates."); return false; }
      if (!(form.details?.short_summary || "").trim()) { toast.error("Short Summary is required for on-page updates."); return false; }
    }
    return true;
  };

  const submit = async (addAnother = false) => {
    if (!validate()) return;
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/activities/${initial.id}`, buildPayload());
        toast.success("Activity updated.", { description: "Your changes have been saved." });
        onSaved({ closeAfter: true });
      } else {
        await api.post("/activities", { project_id: projectId, ...buildPayload() });
        toast.success("Activity added successfully.", { description: "This activity will now appear in your SEO Activity Timeline." });
        if (addAnother) {
          setForm(blankForm({ activity_type: form.activity_type, activity_date: form.activity_date }));
          setShowBacklinkDetails(BACKLINK_TYPES.has(form.activity_type));
          onSaved({ closeAfter: false });
        } else {
          onSaved({ closeAfter: true });
        }
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl my-8 md:my-0 flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
        data-testid="activity-modal"
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-[#E2E8F0] flex items-start justify-between gap-4">
          <div>
            <div className="text-xl font-display font-semibold text-[#0A0D14]">{isEdit ? "Edit SEO Activity" : "Add SEO Activity"}</div>
            <div className="mt-1 text-sm text-[#525B6A] leading-relaxed">
              Record SEO actions, backlink events, indexing dates, and important notes so you can track ranking changes with more context.
            </div>
          </div>
          <button onClick={onClose} data-testid="activity-modal-close" className="p-1 text-[#8A94A6] hover:text-[#0A0D14] shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-5">
          {/* Activity Type */}
          <Field label="Activity Type" required helper="Choose the kind of SEO work you're logging.">
            <select
              data-testid="activity-type"
              value={form.activity_type}
              onChange={(e) => set({ activity_type: e.target.value })}
              className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm bg-white"
            >
              <option value="">Select an activity type</option>
              {TYPE_OPTIONS.map((k) => <option key={k} value={k}>{TYPE_META[k].label}</option>)}
            </select>
          </Field>

          {/* Title */}
          <Field label="Title" required helper="Use a short title to describe the activity.">
            <input
              data-testid="activity-title"
              value={form.title}
              onChange={(e) => set({ title: e.target.value })}
              placeholder="e.g. Guest post published on niche blog"
              className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm"
            />
          </Field>

          {/* Date + Project */}
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Activity Date" required helper="Choose the date this activity happened.">
              <input
                type="date"
                data-testid="activity-date"
                value={form.activity_date}
                onChange={(e) => set({ activity_date: e.target.value })}
                className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm"
              />
            </Field>
            <Field label="Project" helper="This activity will be saved under the project you're viewing.">
              <div
                data-testid="activity-project"
                className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm bg-[#F8F9FA] text-[#0A0D14] flex items-center justify-between"
              >
                <span className="truncate">{projectName || "Current project"}</span>
                <Info className="h-3.5 w-3.5 text-[#8A94A6] shrink-0 ml-2" />
              </div>
            </Field>
          </div>

          {/* Keyword + Status */}
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Related Keyword" optional helper="Link this activity to a keyword if relevant.">
              <select
                data-testid="activity-keyword"
                value={form.keyword_id}
                onChange={(e) => set({ keyword_id: e.target.value })}
                className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm bg-white"
              >
                <option value="">Select a keyword</option>
                {keywords.map((k) => <option key={k.id} value={k.id}>{k.keyword}</option>)}
              </select>
            </Field>
            <Field label="Status" optional helper="Where this activity stands right now.">
              <select
                data-testid="activity-status"
                value={form.status}
                onChange={(e) => set({ status: e.target.value })}
                className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm bg-white"
              >
                <option value="">Not set</option>
                {STATUS_OPTIONS.map((k) => <option key={k} value={k}>{STATUS_META[k].label}</option>)}
              </select>
            </Field>
          </div>

          {/* Target Page URL */}
          <Field label="Target Page URL" optional={form.activity_type !== "onpage_update"} required={form.activity_type === "onpage_update" ? false : false} helper="Add the page affected by this activity.">
            <input
              data-testid="activity-page-url"
              value={form.page_url}
              onChange={(e) => set({ page_url: e.target.value })}
              placeholder="/seo-guide or full URL"
              className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm"
            />
          </Field>

          {/* On-Page SEO update details — only shown when the activity type is on-page */}
          {form.activity_type === "onpage_update" && (
            <OnPageDetailsSection
              details={form.details}
              onChange={setDetails}
              keywords={keywords}
            />
          )}

          {/* Guest post details — richer, sectioned form with 30+ optional fields. */}
          {RICH_LINK_TYPES.has(form.activity_type) && (
            <GuestPostDetailsSection
              form={form}
              onChangeTop={set}
              details={form.details}
              onChangeDetails={setDetails}
              kind={form.activity_type}
            />
          )}

          {/* Activity Impact Tracking — opt-in observational tracker for
              backlink / guest post. Renders below the rich section so users
              read the metrics first, then decide whether to monitor impact. */}
          {RICH_LINK_TYPES.has(form.activity_type) && (
            <ImpactTrackingSection form={form} onChange={set} />
          )}

          {/* Backlink & source details — only shown for backlink types (or when the record already has backlink fields) */}
          {(BACKLINK_TYPES.has(form.activity_type) || (backlinkFieldsFilled && !RICH_LINK_TYPES.has(form.activity_type))) && (
          <div className="border border-[#E2E8F0] rounded-md">
            <button
              type="button"
              data-testid="activity-toggle-backlink"
              onClick={() => setShowBacklinkDetails((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#F8F9FA] transition"
            >
              <div className="flex flex-col">
                <span className="text-sm font-medium text-[#0A0D14]">Backlink &amp; source details</span>
                <span className="text-[11px] text-[#8A94A6]">Referring URL, supplier, anchor text, published &amp; indexed dates</span>
              </div>
              <span className="text-xs text-[#525B6A]">{showBacklinkDetails ? "Hide" : "Show"}</span>
            </button>
            {showBacklinkDetails && (
              <div className="px-4 pb-4 pt-1 space-y-4 border-t border-[#E2E8F0]" data-testid="activity-backlink-section">
                <Field label="Referring URL" optional helper="Add the backlink or source page URL if applicable.">
                  <input
                    data-testid="activity-referring-url"
                    value={form.referring_url}
                    onChange={(e) => set({ referring_url: e.target.value })}
                    placeholder="https://example.com/guest-post"
                    className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm"
                  />
                </Field>
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Supplier / Source" optional helper="Record the vendor, supplier, or source of the activity.">
                    <input
                      data-testid="activity-supplier"
                      value={form.supplier}
                      onChange={(e) => set({ supplier: e.target.value })}
                      placeholder="e.g. ABC Outreach"
                      className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm"
                    />
                  </Field>
                  <Field label="Anchor Text" optional>
                    <input
                      data-testid="activity-anchor"
                      value={form.anchor_text}
                      onChange={(e) => set({ anchor_text: e.target.value })}
                      placeholder="e.g. best seo guide"
                      className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm"
                    />
                  </Field>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Published Date" optional helper="Use this for backlinks or guest posts that were published on a specific date.">
                    <input
                      type="date"
                      data-testid="activity-published-at"
                      value={form.published_at}
                      onChange={(e) => set({ published_at: e.target.value })}
                      className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm"
                    />
                  </Field>
                  <Field label="Indexed Date" optional helper="Record when the backlink or page was indexed, if known.">
                    <input
                      type="date"
                      data-testid="activity-indexed-at"
                      value={form.indexed_at}
                      onChange={(e) => set({ indexed_at: e.target.value })}
                      className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm"
                    />
                  </Field>
                </div>
              </div>
            )}
          </div>
          )}

          {/* Notes */}
          <Field label="Notes" helper="Example: Guest post was published on a DR40 niche site. Monitor rankings after indexing.">
            <textarea
              rows={4}
              data-testid="activity-notes"
              value={form.notes}
              onChange={(e) => set({ notes: e.target.value })}
              placeholder="Add details about what was done, why it matters, or what to monitor next."
              className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm"
            />
          </Field>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#E2E8F0] bg-[#F8F9FA] flex items-center justify-end gap-2 flex-wrap">
          <button
            data-testid="activity-cancel"
            onClick={onClose}
            className="btn-ghost px-4 py-2 rounded-md text-sm"
          >
            Cancel
          </button>
          {!isEdit && (
            <button
              data-testid="activity-save-and-another"
              onClick={() => submit(true)}
              disabled={busy}
              className="px-4 py-2 rounded-md text-sm border border-[#0047FF] text-[#0047FF] hover:bg-[#0047FF]/[0.06] disabled:opacity-50"
            >
              {busy ? "Saving…" : "Save & Add Another"}
            </button>
          )}
          <button
            data-testid="activity-submit"
            onClick={() => submit(false)}
            disabled={busy}
            className="btn-primary px-4 py-2 rounded-md text-sm disabled:opacity-50"
          >
            {busy ? "Saving…" : (isEdit ? "Save Changes" : "Save Activity")}
          </button>
        </div>
      </div>
    </div>
  );
}

// Small labelled-field wrapper for consistent spacing + optional/required affordances.
function Field({ label, required, optional, helper, children }) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-xs font-medium text-[#0A0D14] mb-1.5">
        <span>{label}</span>
        {required && <span className="text-red-500">*</span>}
        {optional && <span className="text-[10px] text-[#8A94A6] uppercase tracking-wider font-normal">Optional</span>}
      </label>
      {children}
      {helper && <div className="mt-1 text-[11px] text-[#8A94A6] leading-snug">{helper}</div>}
    </div>
  );
}

// -------- Activity Impact Tracking (Guest Post + Backlink only) --------
// Observational tracking: baseline rank + 7/14/30/60-day checkpoints.
// Presented as correlation, never causation — matching the backend contract.
const IMPACT_CHECKPOINT_OPTIONS = [7, 14, 30, 60];
const IMPACT_TRIGGER_SOURCES = [
  { key: "auto",            label: "Auto (indexed → published → activity)" },
  { key: "indexed_date",    label: "Indexed date" },
  { key: "published_date",  label: "Published date" },
  { key: "activity_date",   label: "Activity date" },
];
const IMPACT_LABEL_META = {
  positive_movement:  { text: "Positive movement observed",  cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  negative_movement:  { text: "Negative movement observed",  cls: "bg-red-50 text-red-700 border-red-200" },
  mixed_movement:     { text: "Mixed movement observed",     cls: "bg-amber-50 text-amber-800 border-amber-200" },
  no_clear_movement:  { text: "No clear movement observed",  cls: "bg-slate-100 text-slate-600 border-slate-200" },
  insufficient_data:  { text: "Insufficient data",           cls: "bg-slate-50 text-slate-500 border-slate-200" },
};
const IMPACT_TRANSPARENCY = "Ranking changes after a guest post or backlink may be influenced by multiple SEO factors. SerpNex shows observed movement after this activity date — treat this as correlation, not guaranteed causation.";

function ImpactTrackingSection({ form, onChange }) {
  const enabled = !!form.impact_tracking_enabled;
  const checkpoints = form.checkpoints || [];
  const toggleCheckpoint = (n) => {
    const next = checkpoints.includes(n) ? checkpoints.filter((x) => x !== n) : [...checkpoints, n].sort((a, b) => a - b);
    onChange({ checkpoints: next });
  };
  return (
    <div className="border border-[#E2E8F0] rounded-md" data-testid="impact-tracking-section">
      <button
        type="button"
        data-testid="impact-tracking-toggle"
        onClick={() => onChange({ impact_tracking_enabled: !enabled })}
        className={`w-full flex items-start gap-3 px-4 py-3 text-left transition ${enabled ? "bg-[#0047FF]/[0.04] border-b border-[#0047FF]/15" : "hover:bg-[#F8F9FA]"}`}
      >
        <div className={`mt-0.5 h-4 w-7 rounded-full transition ${enabled ? "bg-[#0047FF]" : "bg-[#E2E8F0]"} relative shrink-0`} aria-hidden>
          <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${enabled ? "left-3.5" : "left-0.5"}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-[#0A0D14]">Monitor ranking impact for this activity</div>
          <div className="text-[11px] text-[#525B6A] mt-0.5">Snapshot the keyword&apos;s rank before this activity and observe it 7 / 14 / 30 / 60 days later. Observational only — not proof of causation.</div>
        </div>
      </button>

      {enabled && (
        <div className="p-4 space-y-4" data-testid="impact-tracking-body">
          <Field label="Trigger date source" helper="Which date starts the checkpoint clock. 'Auto' picks the best available: indexed → published → activity.">
            <select
              data-testid="impact-trigger-source"
              value={form.trigger_date_source || "auto"}
              onChange={(e) => onChange({ trigger_date_source: e.target.value })}
              className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white md:max-w-md"
            >
              {IMPACT_TRIGGER_SOURCES.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
          </Field>

          <div>
            <div className="text-xs font-medium text-[#0A0D14] mb-2">Checkpoints to observe</div>
            <div className="flex flex-wrap gap-2" data-testid="impact-checkpoints">
              {IMPACT_CHECKPOINT_OPTIONS.map((n) => {
                const on = checkpoints.includes(n);
                return (
                  <button
                    type="button"
                    key={n}
                    data-testid={`impact-checkpoint-${n}`}
                    onClick={() => toggleCheckpoint(n)}
                    className={`px-3 py-1.5 rounded-md text-xs border transition ${on ? "border-[#0047FF] bg-[#0047FF]/[0.06] text-[#0047FF]" : "border-[#E2E8F0] text-[#525B6A] hover:border-[#0047FF]/40"}`}
                  >
                    {`After ${n} days`}
                  </button>
                );
              })}
            </div>
            <div className="mt-2 text-[11px] text-[#8A94A6]">
              Baseline rank (before activity) is always captured.
              {!form.keyword_id && <span className="ml-1 text-amber-700">Tip: link a keyword above so we can pull rank history.</span>}
            </div>
          </div>

          <div className="rounded-md bg-[#F8F9FA] border border-[#E2E8F0] p-3 text-[11px] text-[#525B6A] leading-relaxed" data-testid="impact-transparency">
            <Info className="h-3.5 w-3.5 inline mr-1 -mt-0.5 text-[#8A94A6]" />
            {IMPACT_TRANSPARENCY}
          </div>
        </div>
      )}
    </div>
  );
}

// Compact impact tracking card shown on the timeline activity feed. Shows the
// observed movement label + net change, with an expandable checkpoint table.
function ImpactCard({ impact }) {
  const [open, setOpen] = useState(false);
  if (!impact || !impact.enabled) return null;
  const meta = IMPACT_LABEL_META[impact.label] || IMPACT_LABEL_META.insufficient_data;
  const net = impact.net_change;
  const netDir = net == null ? null : net > 0 ? "up" : net < 0 ? "down" : "flat";
  const netAbs = net == null ? null : Math.abs(net);

  return (
    <div className="mt-3 rounded-md border border-[#E2E8F0] overflow-hidden" data-testid="impact-card">
      <div className="px-3 py-2 bg-[#F8F9FA] border-b border-[#E2E8F0] flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-[#8A94A6] font-medium">Impact tracking</span>
          <span className={`inline-flex items-center text-[10px] px-2 py-0.5 rounded border font-medium uppercase tracking-wider ${meta.cls}`} data-testid="impact-label">
            {meta.text}
          </span>
          {net != null && (
            <span
              data-testid="impact-net"
              className={`inline-flex items-center gap-1 text-[11px] tabular-nums px-2 py-0.5 rounded border ${netDir === "up" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : netDir === "down" ? "bg-red-50 text-red-700 border-red-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}
            >
              {netDir === "up" && <><ArrowUpRight className="h-3 w-3" />+{netAbs} positions</>}
              {netDir === "down" && <><ArrowDownRight className="h-3 w-3" />−{netAbs} positions</>}
              {netDir === "flat" && <><Minus className="h-3 w-3" />No change</>}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="impact-toggle-details"
          className="text-[11px] text-[#0047FF] hover:underline"
        >
          {open ? "Hide details" : "View details"}
        </button>
      </div>

      <div className="px-3 py-2 text-[11px] text-[#525B6A] flex flex-wrap items-center gap-x-4 gap-y-1">
        {impact.trigger_date && (
          <span data-testid="impact-trigger">
            Trigger date: <span className="text-[#0A0D14] font-medium">{impact.trigger_date}</span>
            <span className="ml-1 text-[10px] uppercase tracking-wider text-[#8A94A6]">({impact.trigger_source?.replace("_", " ") || "auto"})</span>
          </span>
        )}
        {impact.baseline?.rank != null && (
          <span data-testid="impact-baseline">
            Baseline: <span className="text-[#0A0D14] font-medium tabular-nums">#{impact.baseline.rank}</span>
          </span>
        )}
        {impact.best_rank != null && (
          <span data-testid="impact-best">
            Best observed: <span className="text-[#0A0D14] font-medium tabular-nums">#{impact.best_rank}</span>
          </span>
        )}
      </div>

      {open && (
        <div className="px-3 pt-1 pb-3" data-testid="impact-details">
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-[#8A94A6]">
                  <th className="text-left font-medium py-1.5 pr-3">Checkpoint</th>
                  <th className="text-left font-medium py-1.5 pr-3">Target date</th>
                  <th className="text-left font-medium py-1.5 pr-3">Rank</th>
                  <th className="text-left font-medium py-1.5 pr-3">vs Baseline</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-[#E2E8F0]">
                  <td className="py-1.5 pr-3 text-[#0A0D14] font-medium">Before activity</td>
                  <td className="py-1.5 pr-3 text-[#525B6A]">{impact.baseline?.date || "—"}</td>
                  <td className="py-1.5 pr-3 text-[#0A0D14] tabular-nums">{impact.baseline?.rank != null ? `#${impact.baseline.rank}` : "—"}</td>
                  <td className="py-1.5 pr-3 text-[#8A94A6]">baseline</td>
                </tr>
                {(impact.checkpoints || []).map((c) => {
                  const dirCls = c.delta == null ? "text-[#8A94A6]" : c.delta > 0 ? "text-emerald-600" : c.delta < 0 ? "text-red-600" : "text-[#8A94A6]";
                  return (
                    <tr key={c.offset_days} className="border-t border-[#E2E8F0]" data-testid={`impact-row-${c.offset_days}`}>
                      <td className="py-1.5 pr-3 text-[#0A0D14]">After {c.offset_days} days</td>
                      <td className="py-1.5 pr-3 text-[#525B6A]">{c.target_date}</td>
                      <td className="py-1.5 pr-3 text-[#0A0D14] tabular-nums">
                        {c.rank != null ? `#${c.rank}` : (c.status === "pending" ? <span className="text-[10px] uppercase tracking-wider text-[#8A94A6]">Pending</span> : <span className="text-[10px] uppercase tracking-wider text-[#8A94A6]">No data</span>)}
                      </td>
                      <td className={`py-1.5 pr-3 tabular-nums ${dirCls}`}>
                        {c.delta == null ? "—" : c.delta > 0 ? `+${c.delta}` : `${c.delta}`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-3 text-[10px] text-[#8A94A6] leading-relaxed italic" data-testid="impact-details-transparency">
            {impact.transparency || IMPACT_TRANSPARENCY}
          </div>
        </div>
      )}
    </div>
  );
}

// On-Page SEO Update — type-specific section rendered inside the modal when
// activity_type === "onpage_update". Fields are tiered: required, recommended,
// then advanced (before/after diff, word counts, schema, priority) hidden by
// default to keep the form clean.
function OnPageDetailsSection({ details, onChange, keywords }) {
  const d = details || {};
  const set = onChange;
  const [showAdvanced, setShowAdvanced] = useState(() => {
    // Auto-open advanced if the record already has any advanced field filled.
    const adv = ["before_summary","after_summary","title_tag_before","title_tag_after","meta_description_before","meta_description_after","h1_before","h1_after","word_count_before","word_count_after","schema_type","internal_links_added","updated_by","priority"];
    return adv.some((k) => d[k] != null && d[k] !== "");
  });

  return (
    <div className="border border-[#0047FF]/25 bg-[#0047FF]/[0.03] rounded-md" data-testid="activity-onpage-section">
      <div className="px-4 py-3 border-b border-[#0047FF]/15">
        <div className="text-sm font-medium text-[#0A0D14]">On-Page SEO update details</div>
        <div className="text-[11px] text-[#525B6A]">Capture what changed on the page, why, and the expected outcome — makes it easy to correlate with ranking movement later.</div>
      </div>
      <div className="p-4 space-y-4">
        {/* --- Required tier --- */}
        <Field label="Update Type" required helper="What specifically did you change on the page?">
          <select
            data-testid="onpage-update-type"
            value={d.update_type || ""}
            onChange={(e) => set({ update_type: e.target.value })}
            className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm bg-white"
          >
            <option value="">Select update type</option>
            {ONPAGE_UPDATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Short Summary" required helper="One line: what did you change on this page?">
          <input
            data-testid="onpage-short-summary"
            value={d.short_summary || ""}
            onChange={(e) => set({ short_summary: e.target.value })}
            placeholder="e.g. Rewrote intro + added FAQ block targeting 3 long-tail keywords"
            className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm"
          />
        </Field>

        {/* --- Recommended tier --- */}
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Reason for Update" optional helper="Why did you make this change?">
            <select
              data-testid="onpage-reason"
              value={d.reason || ""}
              onChange={(e) => set({ reason: e.target.value })}
              className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm bg-white"
            >
              <option value="">Not set</option>
              {ONPAGE_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Expected Outcome" optional helper="What are you hoping this change achieves?">
            <select
              data-testid="onpage-expected-outcome"
              value={d.expected_outcome || ""}
              onChange={(e) => set({ expected_outcome: e.target.value })}
              className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-md text-sm bg-white"
            >
              <option value="">Not set</option>
              {ONPAGE_OUTCOMES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Related Keywords" optional helper="Hold Ctrl / Cmd to select more than one.">
          <select
            multiple
            size={Math.min(5, Math.max(3, keywords.length))}
            data-testid="onpage-related-keywords"
            value={d.related_keywords || []}
            onChange={(e) => set({ related_keywords: Array.from(e.target.selectedOptions).map((o) => o.value) })}
            className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white"
          >
            {keywords.map((k) => <option key={k.id} value={k.id}>{k.keyword}</option>)}
          </select>
        </Field>

        {/* --- Advanced tier (collapsed by default) --- */}
        <div className="border-t border-[#0047FF]/15 pt-3">
          <button
            type="button"
            data-testid="onpage-toggle-advanced"
            onClick={() => setShowAdvanced((v) => !v)}
            className="text-xs text-[#0047FF] hover:underline inline-flex items-center gap-1"
          >
            {showAdvanced ? "Hide" : "Show"} advanced fields (before/after diffs, word counts, schema, priority)
          </button>
          {showAdvanced && (
            <div className="mt-4 space-y-4" data-testid="onpage-advanced">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Before Summary" optional>
                  <textarea rows={2} data-testid="onpage-before-summary" value={d.before_summary || ""} onChange={(e) => set({ before_summary: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
                </Field>
                <Field label="After Summary" optional>
                  <textarea rows={2} data-testid="onpage-after-summary" value={d.after_summary || ""} onChange={(e) => set({ after_summary: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
                </Field>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Title Tag Before" optional>
                  <input data-testid="onpage-title-tag-before" value={d.title_tag_before || ""} onChange={(e) => set({ title_tag_before: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
                </Field>
                <Field label="Title Tag After" optional>
                  <input data-testid="onpage-title-tag-after" value={d.title_tag_after || ""} onChange={(e) => set({ title_tag_after: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
                </Field>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Meta Description Before" optional>
                  <textarea rows={2} data-testid="onpage-meta-before" value={d.meta_description_before || ""} onChange={(e) => set({ meta_description_before: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
                </Field>
                <Field label="Meta Description After" optional>
                  <textarea rows={2} data-testid="onpage-meta-after" value={d.meta_description_after || ""} onChange={(e) => set({ meta_description_after: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
                </Field>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="H1 Before" optional>
                  <input data-testid="onpage-h1-before" value={d.h1_before || ""} onChange={(e) => set({ h1_before: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
                </Field>
                <Field label="H1 After" optional>
                  <input data-testid="onpage-h1-after" value={d.h1_after || ""} onChange={(e) => set({ h1_after: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
                </Field>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Word Count Before" optional>
                  <input type="number" min="0" data-testid="onpage-wc-before" value={d.word_count_before ?? ""} onChange={(e) => set({ word_count_before: e.target.value === "" ? "" : Number(e.target.value) })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
                </Field>
                <Field label="Word Count After" optional>
                  <input type="number" min="0" data-testid="onpage-wc-after" value={d.word_count_after ?? ""} onChange={(e) => set({ word_count_after: e.target.value === "" ? "" : Number(e.target.value) })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
                </Field>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Schema Type" optional>
                  <select data-testid="onpage-schema-type" value={d.schema_type || ""} onChange={(e) => set({ schema_type: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
                    <option value="">Not set</option>
                    {SCHEMA_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Internal Links Added" optional>
                  <input type="number" min="0" data-testid="onpage-internal-links" value={d.internal_links_added ?? ""} onChange={(e) => set({ internal_links_added: e.target.value === "" ? "" : Number(e.target.value) })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
                </Field>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Updated By" optional helper="Person or team member who made the change.">
                  <input data-testid="onpage-updated-by" value={d.updated_by || ""} onChange={(e) => set({ updated_by: e.target.value })} placeholder="e.g. Alex Chen" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
                </Field>
                <Field label="Priority" optional>
                  <select data-testid="onpage-priority" value={d.priority || ""} onChange={(e) => set({ priority: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
                    <option value="">Not set</option>
                    {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </Field>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// Guest Post + Backlink Built activities share the same sectioned form (all
// fields optional per spec). Basic details are expanded by default; the four
// deeper sections are collapsed so the form feels approachable. When `kind`
// is "backlink_built" a Backlink Type dropdown is added and the section /
// URL labels are swapped to backlink-specific wording.
function GuestPostDetailsSection({ form, onChangeTop, details, onChangeDetails, kind = GUEST_POST_TYPE }) {
  const isBacklinkBuilt = kind === BACKLINK_BUILT_TYPE;
  const d = details || {};
  const setD = onChangeDetails;
  const setTop = onChangeTop;

  const has = (keys) => keys.some((k) => (d[k] != null && d[k] !== "") || (form[k] != null && form[k] !== ""));
  const [openIndex,      setOpenIndex]      = useState(true);
  const [openPlacement,  setOpenPlacement]  = useState(has(["link_position", "context", "outbound_links", "images_included", "internal_links_count"]));
  const [openAuthority,  setOpenAuthority]  = useState(has(["dr","da","semrush_as","tf","cf","organic_traffic","organic_keywords","referring_domains","spam_score","country","language"]));
  const [openRelevance,  setOpenRelevance]  = useState(has(["relevance", "niche", "ai_generated", "human_written"]));

  const numChange = (key) => (e) => setD({ [key]: e.target.value === "" ? "" : Number(e.target.value) });

  const sectionTitle = isBacklinkBuilt ? "Backlink details" : "Guest post details";
  const urlLabel = isBacklinkBuilt ? "Backlink URL" : "Guest Post URL";
  const urlPlaceholder = isBacklinkBuilt ? "https://example.com/backlink-page" : "https://example.com/guest-post";
  const urlHelper = isBacklinkBuilt ? "The page where the backlink lives." : "The published guest post URL.";

  return (
    <div className="border border-[#0047FF]/25 bg-[#0047FF]/[0.03] rounded-md" data-testid={isBacklinkBuilt ? "activity-backlink-built-section" : "activity-guestpost-section"}>
      <div className="px-4 py-3 border-b border-[#0047FF]/15">
        <div className="text-sm font-medium text-[#0A0D14]">{sectionTitle}</div>
        <div className="text-[11px] text-[#525B6A]">All fields are optional — save even with just a few filled. Title, Target URL and Related Keyword are set at the top of this form.</div>
      </div>

      {/* Section 1 — Basic details (always visible) */}
      <div className="p-4 space-y-4">
        <div className="text-[11px] uppercase tracking-wider text-[#8A94A6] font-medium">Basic details</div>
        {isBacklinkBuilt && (
          <Field label="Backlink Type" optional helper="What kind of backlink is this?">
            <select data-testid="gp-backlink-type" value={d.backlink_type || ""} onChange={(e) => setD({ backlink_type: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
              <option value="">Not set</option>
              {BACKLINK_KIND_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
        )}
        <div className="grid md:grid-cols-2 gap-4">
          <Field label={urlLabel} optional helper={urlHelper}>
            <input data-testid="gp-post-url" value={form.referring_url} onChange={(e) => setTop({ referring_url: e.target.value })} placeholder={urlPlaceholder} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
          </Field>
          <Field label="Anchor Text" optional>
            <input data-testid="gp-anchor" value={form.anchor_text} onChange={(e) => setTop({ anchor_text: e.target.value })} placeholder='e.g. "best seo guide"' className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
          </Field>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Link Type" optional>
            <select data-testid="gp-link-type" value={d.link_type || ""} onChange={(e) => setD({ link_type: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
              <option value="">Not set</option>
              {LINK_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Supplier / Vendor" optional>
            <input data-testid="gp-supplier" value={form.supplier} onChange={(e) => setTop({ supplier: e.target.value })} placeholder="e.g. ABC Outreach" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
          </Field>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Cost" optional>
            <input type="number" min="0" step="0.01" data-testid="gp-cost" value={d.cost ?? ""} onChange={numChange("cost")} placeholder="e.g. 150" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
          </Field>
          <Field label="Currency" optional>
            <select data-testid="gp-currency" value={d.currency || ""} onChange={(e) => setD({ currency: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
              <option value="">Not set</option>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Date Ordered" optional>
            <input type="date" data-testid="gp-date-ordered" value={d.date_ordered || ""} onChange={(e) => setD({ date_ordered: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
          </Field>
        </div>
        <Field label="Date Published" optional>
          <input type="date" data-testid="gp-date-published" value={form.published_at} onChange={(e) => setTop({ published_at: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm md:max-w-xs" />
        </Field>
      </div>

      {/* Section 2 — Indexing details */}
      <GuestPostSubsection title="Indexing details" open={openIndex} onToggle={() => setOpenIndex((v) => !v)} testid="gp-section-indexing">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Indexed Status" optional>
            <select data-testid="gp-indexed-status" value={d.indexed_status || ""} onChange={(e) => setD({ indexed_status: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
              <option value="">Not set</option>
              {INDEXED_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Date Indexed" optional>
            <input type="date" data-testid="gp-date-indexed" value={form.indexed_at} onChange={(e) => setTop({ indexed_at: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
          </Field>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Indexing Method" optional>
            <select data-testid="gp-indexing-method" value={d.indexing_method || ""} onChange={(e) => setD({ indexing_method: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
              <option value="">Not set</option>
              {INDEXING_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
          <Field label="Last Checked" optional>
            <input type="date" data-testid="gp-last-checked" value={d.last_checked || ""} onChange={(e) => setD({ last_checked: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
          </Field>
        </div>
      </GuestPostSubsection>

      {/* Section 3 — Placement / page details */}
      <GuestPostSubsection title="Placement & page details" open={openPlacement} onToggle={() => setOpenPlacement((v) => !v)} testid="gp-section-placement">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Link Position" optional>
            <select data-testid="gp-link-position" value={d.link_position || ""} onChange={(e) => setD({ link_position: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
              <option value="">Not set</option>
              {LINK_POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Context" optional>
            <select data-testid="gp-context" value={d.context || ""} onChange={(e) => setD({ context: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
              <option value="">Not set</option>
              {CONTEXT_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Outbound Links on Page" optional>
            <input type="number" min="0" data-testid="gp-outbound-links" value={d.outbound_links ?? ""} onChange={numChange("outbound_links")} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
          </Field>
          <Field label="Images Included" optional>
            <select data-testid="gp-images-included" value={d.images_included || ""} onChange={(e) => setD({ images_included: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
              <option value="">Not set</option>
              {YES_NO.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
          <Field label="Internal Links Count" optional>
            <input type="number" min="0" data-testid="gp-internal-links-count" value={d.internal_links_count ?? ""} onChange={numChange("internal_links_count")} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
          </Field>
        </div>
      </GuestPostSubsection>

      {/* Section 4 — Authority / SEO metrics */}
      <GuestPostSubsection title="Authority & SEO metrics" open={openAuthority} onToggle={() => setOpenAuthority((v) => !v)} testid="gp-section-authority">
        <div className="grid md:grid-cols-5 gap-3">
          {[
            ["DR", "dr"], ["DA", "da"], ["AS (Semrush)", "semrush_as"], ["TF", "tf"], ["CF", "cf"],
          ].map(([label, key]) => (
            <Field key={key} label={label} optional>
              <input type="number" min="0" data-testid={`gp-${key}`} value={d[key] ?? ""} onChange={numChange(key)} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
            </Field>
          ))}
        </div>
        <div className="grid md:grid-cols-4 gap-3">
          {[
            ["Organic Traffic",   "organic_traffic"],
            ["Organic Keywords",  "organic_keywords"],
            ["Referring Domains", "referring_domains"],
            ["Spam Score",        "spam_score"],
          ].map(([label, key]) => (
            <Field key={key} label={label} optional>
              <input type="number" min="0" data-testid={`gp-${key}`} value={d[key] ?? ""} onChange={numChange(key)} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
            </Field>
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Country" optional helper="e.g. US, MY, or United States.">
            <input data-testid="gp-country" value={d.country || ""} onChange={(e) => setD({ country: e.target.value })} placeholder="e.g. US" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
          </Field>
          <Field label="Language" optional helper="e.g. en, ms, or English.">
            <input data-testid="gp-language" value={d.language || ""} onChange={(e) => setD({ language: e.target.value })} placeholder="e.g. en" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
          </Field>
        </div>
      </GuestPostSubsection>

      {/* Section 5 — Relevance / quality */}
      <GuestPostSubsection title="Relevance & quality" open={openRelevance} onToggle={() => setOpenRelevance((v) => !v)} testid="gp-section-relevance">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Relevance" optional>
            <select data-testid="gp-relevance" value={d.relevance || ""} onChange={(e) => setD({ relevance: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
              <option value="">Not set</option>
              {RELEVANCE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Niche" optional>
            <input data-testid="gp-niche" value={d.niche || ""} onChange={(e) => setD({ niche: e.target.value })} placeholder="e.g. Personal finance" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
          </Field>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="AI Generated?" optional>
            <select data-testid="gp-ai-generated" value={d.ai_generated || ""} onChange={(e) => setD({ ai_generated: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
              <option value="">Not set</option>
              {YES_NO_UNKNOWN.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
          <Field label="Human Written?" optional>
            <select data-testid="gp-human-written" value={d.human_written || ""} onChange={(e) => setD({ human_written: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
              <option value="">Not set</option>
              {YES_NO_UNKNOWN.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
        </div>
      </GuestPostSubsection>
    </div>
  );
}

function GuestPostSubsection({ title, open, onToggle, testid, children }) {
  return (
    <div className="border-t border-[#0047FF]/15">
      <button
        type="button"
        data-testid={testid}
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#0047FF]/[0.04] transition"
      >
        <span className="text-[13px] font-medium text-[#0A0D14]">{title}</span>
        <span className="text-xs text-[#525B6A]">{open ? "Hide" : "Show"}</span>
      </button>
      {open && <div className="px-4 pb-4 space-y-4">{children}</div>}
    </div>
  );
}

// Specialised card for guest post activities — surfaces only the six key
// fields per spec and hides the rest behind a "View details" toggle.
function GuestPostCard({ row, canEdit, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const d = row.details || {};
  const indexed = d.indexed_status || (row.indexed_at ? "Indexed" : null);
  const ctx = row.ranking_context;
  return (
    <div className="card-crisp p-4" data-testid={`activity-card-${row.id}`}>
      <div className="flex items-start gap-3">
        <span className={`shrink-0 inline-flex items-center text-[10px] px-2 py-0.5 rounded border font-medium uppercase tracking-wider ${typeTone("guest_post_published")}`}>
          Guest Post Published
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-[#0A0D14] break-words">{row.title}</div>
          <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
            {row.anchor_text && <SummaryRow label="Anchor" value={<span>&ldquo;{row.anchor_text}&rdquo;</span>} />}
            {row.page_url    && <SummaryRow label="Target page" value={<a href={row.page_url} target="_blank" rel="noreferrer" className="text-[#0047FF] hover:underline break-all">{row.page_url}</a>} />}
            {row.supplier    && <SummaryRow label="Supplier" value={row.supplier} />}
            {row.published_at && <SummaryRow label="Date published" value={fmtDate(row.published_at)} />}
            {indexed         && <SummaryRow label="Indexed status" value={indexed} />}
          </dl>
          <button
            type="button"
            data-testid={`activity-view-details-${row.id}`}
            onClick={() => setExpanded((v) => !v)}
            className="mt-3 text-xs text-[#0047FF] hover:underline"
          >
            {expanded ? "Hide details" : "View details"}
          </button>
          {expanded && <GuestPostDetailsView row={row} />}
          {ctx && (ctx.before || ctx.after) && <RankingContextStrip ctx={ctx} />}
          {row.impact_tracking && row.impact_tracking.enabled && <ImpactCard impact={row.impact_tracking} />}
          {row.note && <div className="mt-2 text-sm text-[#525B6A] whitespace-pre-wrap break-words">{row.note}</div>}
          {row.author_email && (
            <div className="mt-2 text-[10px] text-[#8A94A6] uppercase tracking-wider">
              Logged by {row.author_email} · {fmtDate(row.created_at)}
            </div>
          )}
        </div>
        {canEdit && (
          <div className="flex items-center gap-1 shrink-0">
            <button data-testid={`activity-edit-${row.id}`} onClick={onEdit} className="p-1.5 text-[#8A94A6] hover:text-[#0047FF] hover:bg-[#F8F9FA] rounded" title="Edit">
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button data-testid={`activity-delete-${row.id}`} onClick={onDelete} className="p-1.5 text-[#8A94A6] hover:text-red-600 hover:bg-red-50 rounded" title="Delete">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-baseline gap-2 min-w-0">
      <dt className="text-[10px] uppercase tracking-wider text-[#8A94A6] shrink-0 w-[100px]">{label}</dt>
      <dd className="text-[#0A0D14] min-w-0 break-words">{value}</dd>
    </div>
  );
}

// Full field grid — displayed under "View details". Only rows that have a
// value are rendered to keep the panel focused. For backlink_built the
// URL label switches to "Backlink URL" and Backlink Type is surfaced first.
function GuestPostDetailsView({ row, kind = GUEST_POST_TYPE }) {
  const d = row.details || {};
  const isBacklinkBuilt = kind === BACKLINK_BUILT_TYPE;
  const rows = [
    isBacklinkBuilt ? ["Backlink Type", d.backlink_type, d.backlink_type] : null,
    [isBacklinkBuilt ? "Backlink URL" : "Guest Post URL", row.source_url, row.source_url ? <a href={row.source_url} target="_blank" rel="noreferrer" className="text-[#0047FF] hover:underline break-all">{row.source_url}</a> : null],
    ["Link Type",            d.link_type,          d.link_type],
    ["Cost",                 d.cost,               d.cost != null && d.cost !== "" ? `${d.cost}${d.currency ? " " + d.currency : ""}` : null],
    ["Date Ordered",         d.date_ordered,       fmtDate(d.date_ordered)],
    ["Date Indexed",         row.indexed_at,       fmtDate(row.indexed_at)],
    ["Indexing Method",      d.indexing_method,    d.indexing_method],
    ["Last Checked",         d.last_checked,       fmtDate(d.last_checked)],
    ["Link Position",        d.link_position,      d.link_position],
    ["Context",              d.context,            d.context],
    ["Outbound Links",       d.outbound_links,     d.outbound_links],
    ["Images Included",      d.images_included,    d.images_included],
    ["Internal Links Count", d.internal_links_count, d.internal_links_count],
    ["DR",                   d.dr,                 d.dr],
    ["DA",                   d.da,                 d.da],
    ["AS (Semrush)",         d.semrush_as,         d.semrush_as],
    ["TF",                   d.tf,                 d.tf],
    ["CF",                   d.cf,                 d.cf],
    ["Organic Traffic",      d.organic_traffic,    d.organic_traffic],
    ["Organic Keywords",     d.organic_keywords,   d.organic_keywords],
    ["Referring Domains",    d.referring_domains,  d.referring_domains],
    ["Spam Score",           d.spam_score,         d.spam_score],
    ["Country",              d.country,            d.country],
    ["Language",             d.language,           d.language],
    ["Relevance",            d.relevance,          d.relevance],
    ["Niche",                d.niche,              d.niche],
    ["AI Generated?",        d.ai_generated,       d.ai_generated],
    ["Human Written?",       d.human_written,      d.human_written],
  ].filter(Boolean).filter(([, val]) => val != null && val !== "");

  if (rows.length === 0) {
    return <div className="mt-3 text-xs text-[#8A94A6] italic" data-testid="gp-details-view">No additional details recorded.</div>;
  }

  return (
    <div className="mt-3 rounded border border-[#E2E8F0] bg-[#F8F9FA] p-3" data-testid="gp-details-view">
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
        {rows.map(([label, , display]) => (
          <SummaryRow key={label} label={label} value={display} />
        ))}
      </dl>
    </div>
  );
}

// Bulk-add flow — paste a list of backlink URLs and create one activity per
// URL, applying the same common fields (Backlink Type, Anchor, Supplier, etc.)
// across all of them. Same activity_type applied to every row.
function BulkAddBacklinksModal({ projectId, projectName, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [tab, setTab] = useState("paste"); // "paste" | "upload"
  const [type, setType] = useState(BACKLINK_BUILT_TYPE);
  const [urls, setUrls] = useState("");
  const [common, setCommon] = useState({
    backlink_type: "",
    link_type: "",
    supplier: "",
    anchor_text: "",
    page_url: "",
    activity_date: today,
    published_at: "",
    indexed_at: "",
    indexed_status: "",
  });
  const [busy, setBusy] = useState(false);
  // File upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  // Guest Post Published only allows file upload — paste flow is built for
  // backlink lists specifically. Bulk file import supports both types.
  const fileImportAllowed = type === BACKLINK_BUILT_TYPE || type === GUEST_POST_TYPE;

  const set = (patch) => setCommon((c) => ({ ...c, ...patch }));

  // Split on newlines, commas, or whitespace so users can paste from spreadsheets.
  const urlList = urls
    .split(/[\n,]+/)
    .map((u) => u.trim())
    .filter(Boolean);

  const hostOf = (url) => {
    try { return new URL(url).hostname.replace(/^www\./, ""); }
    catch {
      try { return new URL(`https://${url}`).hostname.replace(/^www\./, ""); }
      catch { return url.slice(0, 40); }
    }
  };

  const typeLabelText = type === BACKLINK_BUILT_TYPE ? "Backlink Built" : "Backlink Indexed";

  const submit = async () => {
    if (urlList.length === 0) { toast.error("Paste at least one URL."); return; }
    if (urlList.length > 200)  { toast.error("Max 200 URLs per bulk add."); return; }
    setBusy(true);
    try {
      const activities = urlList.map((url) => {
        const details = {};
        if (type === BACKLINK_BUILT_TYPE && common.backlink_type) details.backlink_type = common.backlink_type;
        if (common.link_type)      details.link_type = common.link_type;
        if (common.indexed_status) details.indexed_status = common.indexed_status;
        return {
          project_id: projectId,
          activity_type: type,
          title: `${typeLabelText} — ${hostOf(url)}`,
          occurred_at: common.activity_date || today,
          source_url: url,
          page_url:    common.page_url.trim()     || null,
          supplier:    common.supplier.trim()     || null,
          anchor_text: common.anchor_text.trim() || null,
          published_at: common.published_at || null,
          indexed_at:   common.indexed_at   || null,
          details: Object.keys(details).length ? details : null,
        };
      });
      const r = await api.post("/activities/bulk", { activities });
      toast.success(`${r.data.created} backlink${r.data.created === 1 ? '' : 's'} added.`, {
        description: "You can edit each one individually from the timeline.",
      });
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk add failed."); }
    finally { setBusy(false); }
  };

  // File upload path — POSTs multipart/form-data to /activities/import so the
  // backend can parse CSV/XLSX with the same schema as the sample templates.
  const submitFile = async () => {
    if (!uploadFile) { toast.error("Choose a CSV or XLSX file first."); return; }
    if (type === "backlink_indexed") { toast.error("File import is only available for Backlink Built and Guest Post Published."); return; }
    setBusy(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("project_id", projectId);
      fd.append("activity_type", type);
      fd.append("file", uploadFile);
      const r = await api.post("/activities/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadResult(r.data);
      const hasErrors = (r.data.skipped || 0) > 0 || (r.data.errors?.length || 0) > 0;
      if (r.data.created > 0) {
        toast.success(`Imported ${r.data.created} activit${r.data.created === 1 ? 'y' : 'ies'}.`, {
          description: hasErrors ? `${r.data.skipped} row(s) skipped — review below, then close when done.` : "You can edit each one individually from the timeline.",
        });
        // Refresh the timeline in the background. Keep the modal open if any
        // rows were skipped so the user can review the per-row errors panel.
        onSaved({ keepOpen: hasErrors });
      } else {
        toast.error("No rows imported. Check the errors below.");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl my-8 md:my-0 flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
        data-testid="bulk-modal"
      >
        <div className="px-6 py-5 border-b border-[#E2E8F0] flex items-start justify-between gap-4">
          <div>
            <div className="text-xl font-display font-semibold text-[#0A0D14]">Bulk Add Backlinks</div>
            <div className="mt-1 text-sm text-[#525B6A] leading-relaxed">
              Paste a list of backlink URLs and apply the same common fields to all of them. One activity is created per URL — you can edit each one later.
            </div>
          </div>
          <button onClick={onClose} data-testid="bulk-close" className="p-1 text-[#8A94A6] hover:text-[#0A0D14] shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-5">
          <Field label="Activity Type" required helper="All URLs will be logged as this type.">
            <div className="flex gap-2 flex-wrap" data-testid="bulk-type-toggle">
              {[
                { key: BACKLINK_BUILT_TYPE, label: "Backlink Built" },
                { key: "backlink_indexed", label: "Backlink Indexed" },
                { key: GUEST_POST_TYPE, label: "Guest Post Published" },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  data-testid={`bulk-type-${key}`}
                  onClick={() => setType(key)}
                  className={`px-3 py-2 rounded-md text-sm border transition ${type === key ? "border-[#0047FF] bg-[#0047FF]/[0.06] text-[#0047FF]" : "border-[#E2E8F0] text-[#525B6A] hover:border-[#0047FF]/40"}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </Field>

          {/* Method tabs: paste raw URLs, or upload a spreadsheet */}
          <div className="border-b border-[#E2E8F0] flex items-center gap-1" data-testid="bulk-tabs">
            <button
              data-testid="bulk-tab-paste"
              onClick={() => { setTab("paste"); setUploadResult(null); }}
              className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 transition ${tab === "paste" ? "border-[#0047FF] text-[#0047FF]" : "border-transparent text-[#525B6A] hover:text-[#0A0D14]"}`}
            >
              Paste URLs
            </button>
            <button
              data-testid="bulk-tab-upload"
              onClick={() => { setTab("upload"); setUploadResult(null); }}
              disabled={!fileImportAllowed}
              className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 transition ${tab === "upload" ? "border-[#0047FF] text-[#0047FF]" : "border-transparent text-[#525B6A] hover:text-[#0A0D14]"} ${!fileImportAllowed ? "opacity-40 cursor-not-allowed" : ""}`}
              title={fileImportAllowed ? "" : "File import is only available for Backlink Built and Guest Post Published."}
            >
              Upload CSV / XLSX
            </button>
          </div>

          {tab === "paste" && (
          <>
          <Field label="Backlink URLs" required helper="One URL per line. Commas are OK too. Max 200.">
            <textarea
              data-testid="bulk-urls"
              rows={7}
              value={urls}
              onChange={(e) => setUrls(e.target.value)}
              placeholder={"https://forbes.com/example-post\nhttps://ahrefs.com/blog/silver-guide\nhttps://searchenginejournal.com/mention"}
              className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm font-mono"
            />
            <div className="mt-1 text-[11px] text-[#525B6A]" data-testid="bulk-url-count">
              <span className="tabular-nums font-medium text-[#0A0D14]">{urlList.length}</span> URL{urlList.length === 1 ? '' : 's'} detected
              {urlList.length > 0 && ` · will create ${urlList.length} activit${urlList.length === 1 ? 'y' : 'ies'}`}
            </div>
          </Field>

          <div className="rounded-md border border-[#E2E8F0]">
            <div className="px-4 py-3 border-b border-[#E2E8F0] bg-[#F8F9FA]">
              <div className="text-sm font-medium text-[#0A0D14]">Common fields</div>
              <div className="text-[11px] text-[#525B6A]">Applied to every URL. Leave blank to skip.</div>
            </div>
            <div className="p-4 space-y-4">
              {type === BACKLINK_BUILT_TYPE && (
                <Field label="Backlink Type" optional>
                  <select data-testid="bulk-backlink-type" value={common.backlink_type} onChange={(e) => set({ backlink_type: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
                    <option value="">Not set</option>
                    {BACKLINK_KIND_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
              )}
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Anchor Text" optional helper="Same anchor for all URLs.">
                  <input data-testid="bulk-anchor" value={common.anchor_text} onChange={(e) => set({ anchor_text: e.target.value })} placeholder='e.g. "silver price malaysia"' className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
                </Field>
                <Field label="Link Type" optional>
                  <select data-testid="bulk-link-type" value={common.link_type} onChange={(e) => set({ link_type: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
                    <option value="">Not set</option>
                    {LINK_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Target Page URL" optional helper="The money page all these backlinks point to.">
                  <input data-testid="bulk-page-url" value={common.page_url} onChange={(e) => set({ page_url: e.target.value })} placeholder="/silver-rate-guide" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
                </Field>
                <Field label="Supplier / Vendor" optional>
                  <input data-testid="bulk-supplier" value={common.supplier} onChange={(e) => set({ supplier: e.target.value })} placeholder="e.g. ABC Outreach" className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
                </Field>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <Field label="Activity Date" required>
                  <input type="date" data-testid="bulk-activity-date" value={common.activity_date} onChange={(e) => set({ activity_date: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
                </Field>
                <Field label="Date Published" optional>
                  <input type="date" data-testid="bulk-published-at" value={common.published_at} onChange={(e) => set({ published_at: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
                </Field>
                <Field label="Date Indexed" optional>
                  <input type="date" data-testid="bulk-indexed-at" value={common.indexed_at} onChange={(e) => set({ indexed_at: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
                </Field>
              </div>
              <Field label="Indexed Status" optional>
                <select data-testid="bulk-indexed-status" value={common.indexed_status} onChange={(e) => set({ indexed_status: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white md:max-w-xs">
                  <option value="">Not set</option>
                  {INDEXED_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
            </div>
          </div>

          <div className="text-[11px] text-[#8A94A6]">
            Project: <span className="text-[#0A0D14] font-medium">{projectName || "current project"}</span> · Titles will be auto-generated as
            {' '}<span className="font-mono text-[#525B6A]">&ldquo;{typeLabelText} — hostname&rdquo;</span> — rename any of them from the timeline later.
          </div>
          </>
          )}

          {tab === "upload" && (
            <div className="space-y-4" data-testid="bulk-upload-panel">
              <div className="rounded-md bg-[#F8F9FA] border border-[#E2E8F0] p-4">
                <div className="text-sm font-medium text-[#0A0D14]">Download a sample template</div>
                <div className="text-[11px] text-[#525B6A] mt-1">Fill it in with your backlinks + all the SEO metrics (DR, DA, cost, anchor, etc.), then upload it back here.</div>
                <div className="mt-3 flex gap-2 flex-wrap">
                  <SampleDownloadButton format="csv"  testid="bulk-sample-csv"  label="Download sample CSV"  />
                  <SampleDownloadButton format="xlsx" testid="bulk-sample-xlsx" label="Download sample XLSX" />
                </div>
              </div>

              <Field label="Upload file" required helper="Same columns as the sample above. Extra columns are ignored. Max 500 rows.">
                <input
                  type="file"
                  data-testid="bulk-file-input"
                  accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(e) => { setUploadFile(e.target.files?.[0] || null); setUploadResult(null); }}
                  className="w-full text-sm file:mr-3 file:px-3 file:py-2 file:rounded-md file:border-0 file:bg-[#0047FF]/[0.08] file:text-[#0047FF] file:text-sm file:font-medium file:cursor-pointer"
                />
                {uploadFile && (
                  <div className="mt-2 text-[11px] text-[#525B6A]" data-testid="bulk-file-name">
                    <span className="text-[#0A0D14] font-medium">{uploadFile.name}</span> · {(uploadFile.size / 1024).toFixed(1)} KB
                  </div>
                )}
              </Field>

              {uploadResult && (
                <div className="rounded-md border border-[#E2E8F0] p-3 text-xs" data-testid="bulk-upload-result">
                  <div className="text-[#0A0D14] font-medium">
                    Imported {uploadResult.created} activit{uploadResult.created === 1 ? 'y' : 'ies'}
                    {uploadResult.skipped > 0 && <span className="text-amber-600 ml-2">· Skipped {uploadResult.skipped}</span>}
                  </div>
                  {uploadResult.errors?.length > 0 && (
                    <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                      {uploadResult.errors.map((er, i) => (
                        <div key={i} className="text-[11px] text-red-600">
                          Line {er.line}{er.field ? ` · ${er.field}` : ""}{er.value ? `=${er.value}` : ""} — {er.message}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#E2E8F0] bg-[#F8F9FA] flex items-center justify-end gap-2">
          <button data-testid="bulk-cancel" onClick={onClose} className="btn-ghost px-4 py-2 rounded-md text-sm">Cancel</button>
          <button
            data-testid="bulk-submit"
            onClick={tab === "paste" ? submit : submitFile}
            disabled={busy || (tab === "paste" ? urlList.length === 0 : !uploadFile)}
            className="btn-primary px-4 py-2 rounded-md text-sm disabled:opacity-50"
          >
            {busy
              ? (tab === "paste" ? "Adding…" : "Importing…")
              : tab === "paste"
                ? (urlList.length > 0 ? `Add ${urlList.length} backlink${urlList.length === 1 ? '' : 's'}` : "Add backlinks")
                : "Import file"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Small link-styled button that hits the backend's sample template endpoint.
// The endpoint is unauthenticated (returns static columns only) so a plain
// anchor works without needing to attach the Bearer token.
function SampleDownloadButton({ format, testid, label }) {
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/activities/sample.${format}`;
  return (
    <a
      data-testid={testid}
      href={url}
      download
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border border-[#E2E8F0] bg-white hover:border-[#0047FF] hover:text-[#0047FF] transition"
    >
      <Download className="h-3.5 w-3.5" /> {label}
    </a>
  );
}


